import { Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { IUkhoWorkRow } from "interfaces";
import React from "react";
import { daysAgo, formatDay, shortServices } from "./lib";

/**
 * The columns "Left but not parked" and "Back but still parked" share
 * (spec 11.2). House table pattern: every column has a fixed width and the
 * long ones wrap (`wrap-text`), so the table never scrolls sideways.
 */

interface WorkTableColumnOptions {
  /** "Left per UKHO" or "Back per UKHO". */
  dayColumnTitle: string;
  renderCustomer: (row: IUkhoWorkRow) => React.ReactNode;
  renderAction: (row: IUkhoWorkRow) => React.ReactNode;
  today: Date;
}

/** A small grey line under a cell's main text (the IMO, the transfer number). */
export const SecondaryLine: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div>
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {children}
    </Typography.Text>
  </div>
);

/** A chip whose text may wrap inside a narrow column. */
export const WrappingTag: React.FC<{
  color?: string;
  children: React.ReactNode;
}> = ({ color, children }) => (
  <Tag color={color} style={{ whiteSpace: "normal" }}>
    {children}
  </Tag>
);

export const buildWorkTableColumns = ({
  dayColumnTitle,
  renderCustomer,
  renderAction,
  today,
}: WorkTableColumnOptions): ColumnsType<IUkhoWorkRow> => [
  {
    title: "Ship",
    key: "ship",
    width: 200,
    className: "wrap-text",
    render: (_, row) => (
      <>
        <div>{row.vesselName}</div>
        <SecondaryLine>{row.imoNumber}</SecondaryLine>
      </>
    ),
  },
  {
    title: "Customer in eMariner",
    key: "customer",
    width: 220,
    className: "wrap-text",
    render: (_, row) => renderCustomer(row),
  },
  {
    title: dayColumnTitle,
    key: "day",
    width: 140,
    render: (_, row) => (
      <>
        <div>{formatDay(row.day)}</div>
        <SecondaryLine>{`transfer ${row.transferId}`}</SecondaryLine>
      </>
    ),
  },
  {
    title: "Days ago",
    key: "daysAgo",
    width: 100,
    align: "right",
    render: (_, row) => daysAgo(row.day, today),
  },
  {
    title: "Services",
    key: "services",
    width: 150,
    className: "wrap-text",
    render: (_, row) => shortServices(row.services),
  },
  {
    title: "Flat fee",
    key: "flatFee",
    width: 120,
    render: (_, row) =>
      row.flatFee ? <Tag color="purple">flat fee</Tag> : <Tag>pay per use</Tag>,
  },
  {
    title: "Action",
    key: "action",
    width: 220,
    className: "wrap-text",
    render: (_, row) => renderAction(row),
  },
];

/** Greys a handled row's text (its chips keep their colours). */
export const greyHandledRow = (row: IUkhoWorkRow, greyTextColor: string) =>
  row.handled ? { style: { color: greyTextColor } } : {};
